/*
  Workflow Outcomes Utility

  This module provides utilities to extract and work with workflow outcomes.
  Outcomes are extracted from workflow data and stored in Redux for use by:
  1. Edge enrichment (adding precedence to workflow edges)
  2. Terminal recon synthetics (generating outcome nodes)
*/

import type { WorkflowOutcome, JourneyWorkflowMetadata } from '../types';

/**
 * Extract outcomes from cached workflow data
 *
 * This extracts outcome actions from the cached workflow data that's already
 * in Redux. This is more efficient than fetching again from the API.
 *
 * @param cachedWorkflowData - The cached workflow data from Redux
 * @returns Array of outcomes with precedence
 */
export function extractOutcomesFromCachedWorkflow(
  cachedWorkflowData: any,
): WorkflowOutcome[] {
  if (!cachedWorkflowData?.actions) {
    return [];
  }

  // Find all outcome-type actions and extract their precedence
  const outcomeActions = cachedWorkflowData.actions.filter(
    (action: any) => action.type === 'outcome',
  );

  const outcomes = outcomeActions.map((action: any) => ({
    outcome: action.name,
    customer_outcome_id: action.customerOutcomeId || null,
    precedence: action.precedence,
  }));

  return outcomes;
}

/**
 * Get outcomes from workflow metadata
 *
 * Returns the extracted outcomes that were stored when the workflow was cached.
 * Returns empty array if outcomes haven't been extracted yet.
 *
 * @param workflowMeta - The workflow metadata from Redux
 * @returns Array of outcomes with precedence
 */
export function getOutcomesFromWorkflowMetadata(
  workflowMeta: JourneyWorkflowMetadata | undefined,
): WorkflowOutcome[] {
  if (!workflowMeta || !workflowMeta.initialized) {
    return [];
  }

  // Return the stored outcomes (extracted when workflow was cached)
  return workflowMeta.outcomes || [];
}

